import React, { useState, useEffect } from 'react';
import { ClipboardList, DollarSign, AlertTriangle, FileText, GraduationCap, Star, Plus, Trash2, CheckCircle, Clock, XCircle, X, Eye, Search, Calendar, ArrowLeft } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField } from '../../components/ui/jewel-modal';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRPage = ({ getAuthHeaders, currentUser, initialTab, onBack }) => {
  const [tab, setTab] = useState(initialTab || 'expenses');
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [trainingRecords, setTrainingRecords] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const isMgmt = ['admin', 'executive', 'superadmin', 'manager'].includes(currentUser?.role);
  const isExec = ['executive', 'superadmin'].includes(currentUser?.role);

  useEffect(() => { loadTab(); }, [tab]);
  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/admin/staff-accounts`, { headers: getAuthHeaders() }); setStaffList(r.data); } catch (e) {}
  };

  const loadTab = async () => {
    setLoading(true);
    const h = getAuthHeaders();
    try {
      if (tab === 'expenses') { const r = await axios.get(`${BACKEND_URL}/api/hr/expenses`, { headers: h }); setExpenses(r.data); }
      else if (tab === 'incidents') { const r = await axios.get(`${BACKEND_URL}/api/hr/incidents`, { headers: h }); setIncidents(r.data); }
      else if (tab === 'payslips') { const r = await axios.get(`${BACKEND_URL}/api/hr/payslips`, { headers: h }); setPayslips(r.data); }
      else if (tab === 'training') { const r = await axios.get(`${BACKEND_URL}/api/hr/training-records`, { headers: h }); setTrainingRecords(r.data); }
      else if (tab === 'reviews') { const r = await axios.get(`${BACKEND_URL}/api/hr/reviews`, { headers: h }); setReviews(r.data); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const tabs = [
    { id: 'expenses', label: 'Expenses', icon: DollarSign },
    { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
    { id: 'payslips', label: 'Payslips', icon: FileText, visible: isExec || currentUser?.role === 'staff' },
    { id: 'training', label: 'Training Records', icon: GraduationCap },
    { id: 'reviews', label: 'Reviews', icon: Star, visible: isMgmt || currentUser?.role === 'staff' }
  ].filter(t => t.visible !== false);

  return (
    <div className="space-y-6" data-testid="hr-page">
      {/* Header */}
      {/* Jewel HR Centre header */}
      <div className="relative overflow-hidden rounded-[24px] border border-emerald-400/20 bg-gradient-to-br from-[#0a1614] via-[#0a0d1a] to-[#0a0d1a] p-6">
        <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[110px]" style={{ background: 'rgba(16,185,129,0.20)' }} />
        <div className="pointer-events-none absolute -bottom-16 -left-16 w-56 h-56 rounded-full blur-[110px]" style={{ background: 'rgba(0,229,255,0.10)' }} />
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(16,185,129,0.55), transparent)' }} />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {onBack && (
              <button onClick={onBack} data-testid="hr-back-btn"
                className="w-9 h-9 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.08] transition-all">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.30), rgba(0,229,255,0.16))', boxShadow: '0 0 22px -4px rgba(16,185,129,0.6), inset 0 1px 0 rgba(255,255,255,0.08)' }}>
              <ClipboardList className="w-5 h-5 text-emerald-200" strokeWidth={1.6} />
            </div>
            <div>
              <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">People ops</p>
              <h1 className="text-2xl font-light text-white leading-tight">HR <span className="italic font-serif text-emerald-300">Centre</span></h1>
              <p className="text-white/40 text-xs mt-0.5">Expenses · incidents · payslips · training · reviews</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)} data-testid="hr-new-btn"
            className="group relative inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[12px] font-medium text-black overflow-hidden transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #10B981 0%, #14B8A6 100%)', boxShadow: '0 12px 30px -8px rgba(16,185,129,0.55)' }}>
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <Plus className="w-3.5 h-3.5" /> New {tab === 'expenses' ? 'expense' : tab === 'incidents' ? 'report' : tab === 'payslips' ? 'payslip' : tab === 'training' ? 'record' : 'review'}
          </button>
        </div>
      </div>

      {/* Jewel Tab pills */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 jewel-scroll">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          const HEX = { expenses: '#F59E0B', incidents: '#F43F5E', payslips: '#00E5FF', training: '#8A2BE2', reviews: '#10B981' }[t.id] || '#10B981';
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`hr-tab-${t.id}`}
              className={`group relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12px] font-medium tracking-wide transition-all whitespace-nowrap ${active ? 'text-white' : 'text-white/45 hover:text-white/85'}`}
              style={active ? { background: `linear-gradient(135deg, ${HEX}22, ${HEX}08)`, border: `1px solid ${HEX}40`, boxShadow: `0 0 22px -6px ${HEX}` } : { border: '1px solid transparent' }}>
              <Icon className="w-3.5 h-3.5" style={active ? { color: HEX } : {}} />
              {t.label}
              {active && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-[2px] rounded-full" style={{ background: HEX, boxShadow: `0 0 8px ${HEX}` }} />}
            </button>
          );
        })}
      </div>

      {loading ? <div className="text-center py-16 text-slate-500">Loading...</div> : (
        <>
          {/* Expenses Tab */}
          {tab === 'expenses' && <ExpensesView expenses={expenses} isMgmt={isMgmt} getAuthHeaders={getAuthHeaders} reload={loadTab} />}
          {/* Incidents Tab */}
          {tab === 'incidents' && <IncidentsView incidents={incidents} isMgmt={isMgmt} getAuthHeaders={getAuthHeaders} reload={loadTab} />}
          {/* Payslips Tab */}
          {tab === 'payslips' && <PayslipsView payslips={payslips} isExec={isExec} />}
          {/* Training Tab */}
          {tab === 'training' && <TrainingView records={trainingRecords} isMgmt={isMgmt} getAuthHeaders={getAuthHeaders} currentUser={currentUser} reload={loadTab} />}
          {/* Reviews Tab */}
          {tab === 'reviews' && <ReviewsView reviews={reviews} isMgmt={isMgmt} />}
        </>
      )}

      {/* Dynamic Form Dialog */}
      <FormDialog open={showForm} onClose={() => setShowForm(false)} tab={tab} getAuthHeaders={getAuthHeaders} staffList={staffList} isMgmt={isMgmt} isExec={isExec} reload={loadTab} />
    </div>
  );
};

/* ============ SUB-VIEWS ============ */

const ExpensesView = ({ expenses, isMgmt, getAuthHeaders, reload }) => {
  const reviewExpense = async (id, status) => {
    try { await axios.put(`${BACKEND_URL}/api/hr/expenses/${id}/review`, { status }, { headers: getAuthHeaders() }); toast.success(`Expense ${status}`); reload(); } catch (e) { toast.error('Failed'); }
  };
  const statusColors = { pending: 'bg-amber-500/20 text-amber-400', approved: 'bg-emerald-500/20 text-emerald-400', rejected: 'bg-red-500/20 text-red-400' };
  if (expenses.length === 0) return <Empty icon={DollarSign} text="No expenses submitted" />;
  return (
    <div className="space-y-3">
      {expenses.map(e => (
        <div key={e.id} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 hover:border-emerald-500/30 transition-all" data-testid={`expense-${e.id}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-white font-semibold">{e.title}</h3>
                <Badge className={statusColors[e.status] || statusColors.pending}>{e.status}</Badge>
                <Badge className="bg-slate-700/50 text-slate-400">{e.category}</Badge>
              </div>
              <p className="text-slate-400 text-sm">{e.description}</p>
              <p className="text-slate-600 text-xs mt-1">By {e.submitted_by} | {e.date || e.created_at?.split('T')[0]}</p>
            </div>
            <div className="text-right ml-4">
              <p className="text-white font-bold text-lg">${e.amount?.toFixed(2)}</p>
              {isMgmt && e.status === 'pending' && (
                <div className="flex gap-1 mt-2">
                  <button onClick={() => reviewExpense(e.id, 'approved')} className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"><CheckCircle className="w-4 h-4" /></button>
                  <button onClick={() => reviewExpense(e.id, 'rejected')} className="p-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"><XCircle className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const IncidentsView = ({ incidents, isMgmt, getAuthHeaders, reload }) => {
  const [resolving, setResolving] = useState(null);
  const [notes, setNotes] = useState('');
  const resolve = async () => {
    try { await axios.put(`${BACKEND_URL}/api/hr/incidents/${resolving}/resolve`, { resolution_notes: notes }, { headers: getAuthHeaders() }); toast.success('Incident resolved'); setResolving(null); setNotes(''); reload(); } catch (e) { toast.error('Failed'); }
  };
  const sevColors = { low: 'bg-slate-500/20 text-slate-400', medium: 'bg-amber-500/20 text-amber-400', high: 'bg-orange-500/20 text-orange-400', critical: 'bg-red-500/20 text-red-400' };
  const statusColors = { open: 'bg-red-500/20 text-red-400', investigating: 'bg-amber-500/20 text-amber-400', resolved: 'bg-emerald-500/20 text-emerald-400' };
  if (incidents.length === 0) return <Empty icon={AlertTriangle} text="No incidents reported" />;
  return (
    <div className="space-y-3">
      {incidents.map(inc => (
        <div key={inc.id} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 hover:border-orange-500/30 transition-all" data-testid={`incident-${inc.id}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-white font-semibold">{inc.title}</h3>
                <Badge className={sevColors[inc.severity]}>{inc.severity}</Badge>
                <Badge className={statusColors[inc.status] || statusColors.open}>{inc.status}</Badge>
              </div>
              <p className="text-slate-400 text-sm">{inc.description}</p>
              <p className="text-slate-600 text-xs mt-1">Reported by {inc.reporter} | {inc.created_at?.split('T')[0]}</p>
              {inc.resolution_notes && <p className="text-emerald-400/70 text-xs mt-1">Resolution: {inc.resolution_notes}</p>}
            </div>
            {isMgmt && inc.status === 'open' && (
              <Button variant="outline" size="sm" onClick={() => setResolving(inc.id)} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 ml-3">Resolve</Button>
            )}
          </div>
        </div>
      ))}
      <Dialog open={!!resolving} onOpenChange={() => setResolving(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader><DialogTitle>Resolve Incident</DialogTitle></DialogHeader>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Resolution notes..." className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white h-24 resize-none" />
          <Button onClick={resolve} className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white" data-testid="resolve-incident-btn">Resolve</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PayslipsView = ({ payslips, isExec }) => {
  if (payslips.length === 0) return <Empty icon={FileText} text="No payslips available" />;
  return (
    <div className="space-y-3">
      {payslips.map(p => (
        <div key={p.id} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 hover:border-emerald-500/30 transition-all" data-testid={`payslip-${p.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold">{p.period}</h3>
              <p className="text-slate-500 text-sm">{p.staff_username}</p>
            </div>
            <div className="text-right">
              <p className="text-emerald-400 font-bold text-lg">${p.net_pay?.toFixed(2)}</p>
              <p className="text-slate-600 text-xs">Base: ${p.basic_pay?.toFixed(2)} | OT: ${p.overtime?.toFixed(2)} | Bonus: ${p.bonus?.toFixed(2)} | Ded: -${p.deductions?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TrainingView = ({ records, isMgmt, getAuthHeaders, currentUser, reload }) => {
  const markComplete = async (id) => {
    try { await axios.post(`${BACKEND_URL}/api/hr/training-records/${id}/complete`, {}, { headers: getAuthHeaders() }); toast.success('Marked as completed'); reload(); } catch (e) { toast.error('Failed'); }
  };
  if (records.length === 0) return <Empty icon={GraduationCap} text="No training records" />;
  return (
    <div className="space-y-3">
      {records.map(t => {
        const completed = t.completions?.some(c => c.username === currentUser?.username);
        return (
          <div key={t.id} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 hover:border-purple-500/30 transition-all" data-testid={`training-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold">{t.title}</h3>
                  {t.mandatory && <Badge className="bg-red-500/20 text-red-400">Mandatory</Badge>}
                  <Badge className="bg-slate-700/50 text-slate-400">{t.category}</Badge>
                </div>
                <p className="text-slate-400 text-sm">{t.description}</p>
                <p className="text-slate-600 text-xs mt-1">Trainer: {t.trainer || 'N/A'} | {t.date || 'No date'} | {t.completions?.length || 0} completions</p>
              </div>
              {!completed ? (
                <Button variant="outline" size="sm" onClick={() => markComplete(t.id)} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 ml-3">Complete</Button>
              ) : (
                <Badge className="bg-emerald-500/20 text-emerald-400 ml-3">Done</Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ReviewsView = ({ reviews, isMgmt }) => {
  const ratingStars = (n) => Array.from({ length: 5 }, (_, i) => <Star key={i} className={`w-4 h-4 ${i < n ? 'text-amber-400 fill-amber-400' : 'text-slate-700'}`} />);
  if (reviews.length === 0) return <Empty icon={Star} text="No performance reviews" />;
  return (
    <div className="space-y-3">
      {reviews.map(r => (
        <div key={r.id} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 hover:border-amber-500/30 transition-all" data-testid={`review-${r.id}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-white font-semibold">{r.staff_username} - {r.period}</h3>
              <p className="text-slate-600 text-xs">By {r.reviewer} | {r.created_at?.split('T')[0]}</p>
            </div>
            <div className="flex">{ratingStars(r.rating)}</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            {r.strengths && <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3"><p className="text-emerald-400 text-xs font-medium mb-1">Strengths</p><p className="text-slate-400 text-xs">{r.strengths}</p></div>}
            {r.improvements && <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3"><p className="text-amber-400 text-xs font-medium mb-1">Improvements</p><p className="text-slate-400 text-xs">{r.improvements}</p></div>}
            {r.goals && <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-lg p-3"><p className="text-cyan-400 text-xs font-medium mb-1">Goals</p><p className="text-slate-400 text-xs">{r.goals}</p></div>}
          </div>
        </div>
      ))}
    </div>
  );
};

const Empty = ({ icon: Icon, text }) => (
  <div className="text-center py-16 bg-slate-900/40 border border-slate-800/50 rounded-2xl">
    <Icon className="w-12 h-12 text-slate-700 mx-auto mb-3" />
    <p className="text-slate-500">{text}</p>
  </div>
);

/* ============ FORM DIALOG ============ */
const FormDialog = ({ open, onClose, tab, getAuthHeaders, staffList, isMgmt, isExec, reload }) => {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm({}); }, [open, tab]);

  const submit = async () => {
    const h = getAuthHeaders();
    try {
      if (tab === 'expenses') {
        if (!form.title || !form.amount) { toast.error('Title and amount required'); return; }
        await axios.post(`${BACKEND_URL}/api/hr/expenses`, { title: form.title, amount: parseFloat(form.amount), category: form.category || 'other', description: form.description || '', date: form.date || '' }, { headers: h });
        toast.success('Expense submitted');
      } else if (tab === 'incidents') {
        if (!form.title || !form.description) { toast.error('Title and description required'); return; }
        await axios.post(`${BACKEND_URL}/api/hr/incidents`, { title: form.title, description: form.description, severity: form.severity || 'medium', category: form.category || 'general', anonymous: form.anonymous || false }, { headers: h });
        toast.success('Incident reported');
      } else if (tab === 'payslips' && isExec) {
        if (!form.staff_username || !form.period || !form.basic_pay) { toast.error('Staff, period and pay required'); return; }
        await axios.post(`${BACKEND_URL}/api/hr/payslips`, { staff_username: form.staff_username, period: form.period, basic_pay: parseFloat(form.basic_pay), overtime: parseFloat(form.overtime || 0), deductions: parseFloat(form.deductions || 0), bonus: parseFloat(form.bonus || 0), notes: form.notes || '' }, { headers: h });
        toast.success('Payslip created');
      } else if (tab === 'training' && isMgmt) {
        if (!form.title) { toast.error('Title required'); return; }
        await axios.post(`${BACKEND_URL}/api/hr/training-records`, { title: form.title, description: form.description || '', trainer: form.trainer || '', date: form.date || '', duration: form.duration || '', category: form.category || 'general', mandatory: form.mandatory || false }, { headers: h });
        toast.success('Training record created');
      } else if (tab === 'reviews' && isMgmt) {
        if (!form.staff_username || !form.period || !form.rating) { toast.error('Staff, period and rating required'); return; }
        await axios.post(`${BACKEND_URL}/api/hr/reviews`, { staff_username: form.staff_username, period: form.period, rating: parseInt(form.rating), strengths: form.strengths || '', improvements: form.improvements || '', goals: form.goals || '', comments: form.comments || '' }, { headers: h });
        toast.success('Review created');
      }
      onClose();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const titleMap = { expenses: 'Submit expense', incidents: 'Report incident', payslips: 'Create payslip', training: 'Add training', reviews: 'Create review' };
  const accentMap = { expenses: 'amber', incidents: 'rose', payslips: 'cyan', training: 'violet', reviews: 'emerald' };
  return (
    <JewelModal open={open} onOpenChange={(v) => !v && onClose()} icon={ClipboardList} title={titleMap[tab] || 'Submit'} subtitle="People ops · quick entry" accent={accentMap[tab] || 'emerald'} size="lg" testid="hr-form-modal"
      footer={<>
        <JewelButton variant="ghost" onClick={onClose}>Cancel</JewelButton>
        <JewelButton accent={accentMap[tab] || 'emerald'} onClick={submit} icon={CheckCircle} data-testid="hr-submit-btn">Submit</JewelButton>
      </>}>
      <div className="space-y-3">
        {tab === 'expenses' && <>
          <JewelField label="Title" required><JewelInput value={form.title || ''} onChange={e => set('title', e.target.value)} placeholder="Expense title" data-testid="expense-title" /></JewelField>
          <div className="grid grid-cols-2 gap-3">
            <JewelField label="Amount ($)" required><JewelInput type="number" value={form.amount || ''} onChange={e => set('amount', e.target.value)} placeholder="0.00" data-testid="expense-amount" /></JewelField>
            <JewelField label="Category"><JewelSelect value={form.category || 'other'} onChange={e => set('category', e.target.value)}>{['travel', 'meals', 'equipment', 'training', 'other'].map(c => <option key={c} value={c}>{c}</option>)}</JewelSelect></JewelField>
          </div>
          <JewelField label="Description"><JewelTextarea rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} /></JewelField>
        </>}
        {tab === 'incidents' && <>
          <JewelField label="Title" required><JewelInput value={form.title || ''} onChange={e => set('title', e.target.value)} placeholder="Incident title" data-testid="incident-title" /></JewelField>
          <JewelField label="Description" required><JewelTextarea rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} data-testid="incident-description" /></JewelField>
          <div className="grid grid-cols-2 gap-3">
            <JewelField label="Severity"><JewelSelect value={form.severity || 'medium'} onChange={e => set('severity', e.target.value)}>{['low', 'medium', 'high', 'critical'].map(s => <option key={s} value={s}>{s}</option>)}</JewelSelect></JewelField>
            <JewelField label="Category"><JewelSelect value={form.category || 'general'} onChange={e => set('category', e.target.value)}>{['workplace', 'safety', 'harassment', 'theft', 'other'].map(c => <option key={c} value={c}>{c}</option>)}</JewelSelect></JewelField>
          </div>
          <label className="inline-flex items-center gap-2 text-white/70 text-xs"><input type="checkbox" checked={form.anonymous || false} onChange={e => set('anonymous', e.target.checked)} className="w-4 h-4 accent-rose-400" /> Submit anonymously</label>
        </>}
        {tab === 'payslips' && isExec && <>
          <JewelField label="Staff" required><JewelSelect value={form.staff_username || ''} onChange={e => set('staff_username', e.target.value)} data-testid="payslip-staff"><option value="">Select staff</option>{staffList.map(s => <option key={s.username} value={s.username}>{s.display_name || s.username}</option>)}</JewelSelect></JewelField>
          <JewelField label="Period" required><JewelInput value={form.period || ''} onChange={e => set('period', e.target.value)} placeholder="e.g. February 2026" data-testid="payslip-period" /></JewelField>
          <div className="grid grid-cols-2 gap-3">
            <JewelField label="Basic pay" required><JewelInput type="number" value={form.basic_pay || ''} onChange={e => set('basic_pay', e.target.value)} /></JewelField>
            <JewelField label="Overtime"><JewelInput type="number" value={form.overtime || ''} onChange={e => set('overtime', e.target.value)} /></JewelField>
            <JewelField label="Bonus"><JewelInput type="number" value={form.bonus || ''} onChange={e => set('bonus', e.target.value)} /></JewelField>
            <JewelField label="Deductions"><JewelInput type="number" value={form.deductions || ''} onChange={e => set('deductions', e.target.value)} /></JewelField>
          </div>
        </>}
        {tab === 'training' && isMgmt && <>
          <JewelField label="Title" required><JewelInput value={form.title || ''} onChange={e => set('title', e.target.value)} placeholder="Training title" data-testid="training-title" /></JewelField>
          <JewelField label="Description"><JewelTextarea rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} /></JewelField>
          <div className="grid grid-cols-2 gap-3">
            <JewelField label="Trainer"><JewelInput value={form.trainer || ''} onChange={e => set('trainer', e.target.value)} /></JewelField>
            <JewelField label="Date"><JewelInput type="date" value={form.date || ''} onChange={e => set('date', e.target.value)} /></JewelField>
            <JewelField label="Duration"><JewelInput value={form.duration || ''} onChange={e => set('duration', e.target.value)} placeholder="e.g. 2 hours" /></JewelField>
            <JewelField label="Category"><JewelSelect value={form.category || 'general'} onChange={e => set('category', e.target.value)}>{['general', 'safety', 'compliance', 'technical', 'soft-skills'].map(c => <option key={c} value={c}>{c}</option>)}</JewelSelect></JewelField>
          </div>
          <label className="inline-flex items-center gap-2 text-white/70 text-xs"><input type="checkbox" checked={form.mandatory || false} onChange={e => set('mandatory', e.target.checked)} className="w-4 h-4 accent-violet-400" /> Mandatory</label>
        </>}
        {tab === 'reviews' && isMgmt && <>
          <JewelField label="Staff" required><JewelSelect value={form.staff_username || ''} onChange={e => set('staff_username', e.target.value)} data-testid="review-staff"><option value="">Select staff</option>{staffList.map(s => <option key={s.username} value={s.username}>{s.display_name || s.username}</option>)}</JewelSelect></JewelField>
          <JewelField label="Period" required><JewelInput value={form.period || ''} onChange={e => set('period', e.target.value)} placeholder="e.g. Q1 2026" /></JewelField>
          <JewelField label="Rating (1–5)" required><JewelSelect value={form.rating || ''} onChange={e => set('rating', e.target.value)}><option value="">Select</option>{[1,2,3,4,5].map(n => <option key={n} value={n}>{n} Star{n > 1 ? 's' : ''}</option>)}</JewelSelect></JewelField>
          <JewelField label="Strengths"><JewelTextarea rows={2} value={form.strengths || ''} onChange={e => set('strengths', e.target.value)} /></JewelField>
          <JewelField label="Areas for improvement"><JewelTextarea rows={2} value={form.improvements || ''} onChange={e => set('improvements', e.target.value)} /></JewelField>
          <JewelField label="Goals"><JewelTextarea rows={2} value={form.goals || ''} onChange={e => set('goals', e.target.value)} /></JewelField>
        </>}
      </div>
    </JewelModal>
  );
};

export default HRPage;
